import { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

const readInitialCart = () => {
  try {
    const raw = localStorage.getItem('destack_cart');
    if (!raw) return { items: [], storeId: null };
    const parsed = JSON.parse(raw);
    return { items: parsed.items || [], storeId: parsed.storeId || null };
  } catch (_) {
    return { items: [], storeId: null };
  }
};

export const CartProvider = ({ children }) => {
  const initial = readInitialCart();
  const [items, setItems] = useState(initial.items);
  const [storeId, setStoreId] = useState(initial.storeId);

  useEffect(() => {
    localStorage.setItem('destack_cart', JSON.stringify({ items, storeId }));
  }, [items, storeId]);

  const addItem = (product) => {
    // If cart has items from different store, clear it
    if (storeId && storeId !== product.store_id) {
      if (!window.confirm('Seu carrinho tem itens de outra loja. Deseja limpar e adicionar este item?')) {
        return false;
      }
      setItems([]);
    }

    setStoreId(product.store_id);

    const variationKey = product.variation_key || '';
    const itemKey = `${product.id}${variationKey ? '|' + variationKey : ''}`;
    const effectivePrice = product.price ?? product.promo_price ?? 0;

    setItems(prev => {
      const existing = prev.find(item => item.item_key === itemKey);
      if (existing) {
        return prev.map(item =>
          item.item_key === itemKey
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, {
        item_key: itemKey,
        product_id: product.id,
        name: product.name,
        price: product.promo_price ? product.promo_price + (product.selected_variations?.reduce((s, v) => s + (v.price_delta || 0), 0) || 0) : effectivePrice,
        image: product.images?.[0] || '',
        quantity: 1,
        store_id: product.store_id,
        store_name: product.store_name,
        selected_variations: product.selected_variations || [],
      }];
    });
    return true;
  };

  const removeItem = (itemKey) => {
    setItems(prev => {
      const newItems = prev.filter(item => item.item_key !== itemKey && item.product_id !== itemKey);
      if (newItems.length === 0) {
        setStoreId(null);
      }
      return newItems;
    });
  };

  const updateQuantity = (itemKey, quantity) => {
    if (quantity <= 0) {
      removeItem(itemKey);
      return;
    }
    setItems(prev =>
      prev.map(item =>
        (item.item_key === itemKey || item.product_id === itemKey)
          ? { ...item, quantity }
          : item
      )
    );
  };

  const clearCart = () => {
    setItems([]);
    setStoreId(null);
  };

  const total = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider value={{
      items,
      storeId,
      addItem,
      removeItem,
      updateQuantity,
      clearCart,
      total,
      itemCount
    }}>
      {children}
    </CartContext.Provider>
  );
};
